import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';

export default class AccountBillingDetailsService {
    /**
     * Retrieves an accounts billing summary.
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof AccountBillingDetailsService
     */
    static getAccountBillingSummary(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getAccountBillingSummary', [], authHeader);
    }

    /**
     * Retrieves an invoice summary for account.
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getAccountInvoiceStreamInformation(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getAccountInvoiceStreamInformation', [], authHeader);
    }

    /**
     * Retrieves details of invoices for the invoice id.
     *
     * @param {String} invoiceStreamId a public-id of invoice stream data
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getInvoiceStreamDetails(invoiceStreamId, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getInvoiceStreamDetails', [invoiceStreamId], authHeader);
    }

    /**
     * Set a payment instrument as automatic payment instrument.
     *
     * @param {String} invoiceStreamId a public-id of invoice stream data
     * @param {Object} instrument a payment instrument in PaymentInstrumentDTO
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static setPaymentGroupPaymentInformation(invoiceStreamId, instrument, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'),
            'setPaymentGroupPaymentInformation', [invoiceStreamId, instrument], authHeader);
    }

    /**
     * Pay for selected invoices with a payment instrument.
     *
     * @param {Array} invoiceIds array of public-ids of invoices
     * @param {Object} amount a payment amount in BigDecimal
     * @param {Object} instrument a payment instrument in PaymentInstrumentDTO
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static makeDirectBillPayment(invoiceIds, amount, instrument, authHeader = {}) {
        const directBillPaymentDto = {
            invoiceIds,
            amountPaid: amount,
            paymentInstrument: instrument
        };

        return JsonRPCService.send(getProxiedServiceUrl('directBillPayment'),
            'makeDirectBillPayment', [directBillPaymentDto], authHeader);
    }

    /**
     * Retrieves accounts billing delivery method
     *
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static getInvoiceDeliveryMethods(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getInvoiceDeliveryMethods', [], authHeader);
    }

    /**
     * Sets a new billing delivery method for the account
     *
     * @param {String} newDeliveryMethod a delivery method code
     * @param {String} accountNumber an account number to set the delivery method
     * @param {Object} authHeader additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     */
    static setInvoiceDeliveryMethodForAccount(newDeliveryMethod, accountNumber, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'setInvoiceDeliveryMethodForAccount', [newDeliveryMethod, accountNumber], authHeader);
    }

    static getPolicyTermBillingSummary(policyNumber, termNumber, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getPolicyTermBillingSummary', [policyNumber, termNumber], authHeader);
    }

    static editPaymentInstrument(policyNumber, paymentInstrumentId, redirectURL, authHeader = {}) {
        const paymentInstrumentRequestDTOML = {
            sessionUUID: '1',
            policyNumber,
            paymentInstrumentId,
            redirectURL
        };
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'editPaymentInstrument', [paymentInstrumentRequestDTOML], authHeader);
    }
    
    static addPaymentInstrument(policyNumber, paymentMethod, redirectURL, authHeader = {}) {
        const paymentInstrumentRequestDTOML = {
            sessionUUID: '1',
            policyNumber,
            paymentMethod,
            redirectURL
        };
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'getIFrameUrlByPolicyNumber', [paymentInstrumentRequestDTOML], authHeader);
    }

    static getAccountPolicyPeriodsBillingSummary(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getAccountPolicyPeriodsBillingSummary', [], authHeader);
    }

    static getNewDocumentsCount(policyTermEnvelopes, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingDocs'), 'getNewDocumentsCount', 
            [policyTermEnvelopes], authHeader)
    }

    static setDocumentAsViewed(docId, token, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingDocs'), 'setDocumentAsViewed', 
            [docId, token], authHeader)
    }

    static getPaymentInstrumentsForAccount(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'getPaymentInstrumentsForAccount', [], authHeader);
    }

    static getIFrameUrlByAccount(paymentMethod, redirectUrl, token, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'getIFrameUrlByAccount', [paymentMethod, redirectUrl, token], authHeader);
    }

    static deletePaymentInstrumentByAccount(paymentInstrumentId, authHeader = {}) {
        const paymentInstrumentRequestDTOML = {
            sessionUUID: '1',
            paymentInstrumentId
        };
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'deletePaymentInstrumentByAccount', [paymentInstrumentRequestDTOML], authHeader);
    }

    static makePayments(bundledPaymentRequestDTO, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'makePayments', [bundledPaymentRequestDTO], authHeader);
    }

    static makeBankAccountPayment(bundledPaymentRequestDTO, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'makeBankAccountPayment', [bundledPaymentRequestDTO], authHeader);
    }

    static addBankAccountPaymentInstrument(paymentDetails, authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'addBankAccountPaymentInstrumentByEndorsement', [paymentDetails], authHeader);
    }

    static usePaymentusACH(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('policyCommonPayment'), 'usePaymentusACH', [], authHeader);
    }

    static getPoliciesWithInvalidPaymentInstruments(authHeader = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('billingData'), 'getPoliciesWithInvalidPaymentInstruments', [], authHeader);
    }
}
